// /receiving/box/:id — one physical box, the thing with the RB- sticker.
//
// A box holds one or more items (receipt_items). This page never redirects: a
// box of one item is still a box. The hub's scan field is what sends a scan of
// a one-item box straight to the item, where every sticker printed before boxes
// could hold several has always landed.

import React, { useCallback, useEffect, useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import { Tag } from '../components/ui/Tag';
import { Link, useHistory, useParams } from 'react-router-dom';
import BackLink from '../components/BackLink';
import type { ColumnDef } from '@tanstack/react-table';
import { useApi } from '../apiHelpers';
import PageHeader, { SecondaryAction } from '../components/PageHeader';
import { DataTable } from '../components/table/DataTable';
import ItemName, { itemLabel } from '../components/ItemName';
import { useToast } from '../components/Toast/ToastContext';
import useUserStore from '../stores/useUserStore';
import LogoSpinner from '../components/ui/LogoSpinner';
import EmptyState from '../components/ui/EmptyState';
import { formatTimestamp } from '../utils/date-utils';
import { printBoxInstanceQRLabels } from '../utils/printing-utils';
import BoxLocation from './BoxLocation';
import { openAuthedPdf } from './openAuthedPdf';
import type { BoxByBarcode, ReceiptBoxDetail, ReceiptBoxItem } from './types';
import PageLayout from '../components/ui/PageLayout';

interface TableMeta {
  printingId: string | null;
  onPrintQrs: (item: ReceiptBoxItem) => void;
}

const FOCUS =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded';

const ITEM_COLUMNS: ColumnDef<ReceiptBoxItem, any>[] = [
  {
    id: 'item',
    header: 'Item',
    meta: { cellClass: 'max-w-xs' },
    cell: ({ row }) => (
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <ItemName size="sm" item={row.original} />
        {row.original.disposition === 'rejected' && (
          <Tag tone="warn">Not acceptable, at QC Hold</Tag>
        )}
      </div>
    ),
  },
  {
    id: 'received',
    header: 'Received',
    meta: { align: 'right', numeric: true, cellClass: 'text-on-surface' },
    cell: ({ row }) => row.original.quantity_received,
  },
  {
    id: 'remaining',
    header: 'Remaining',
    meta: { align: 'right', numeric: true, cellClass: 'text-on-surface-variant' },
    cell: ({ row }) => row.original.quantity_remaining,
  },
  {
    id: 'location',
    header: 'Location',
    meta: { cellClass: 'text-on-surface-variant whitespace-nowrap' },
    cell: ({ row }) => <BoxLocation where={row.original} />,
  },
  {
    id: 'received_by',
    header: 'Received by',
    meta: { cellClass: 'text-on-surface-variant whitespace-nowrap' },
    cell: ({ row }) => row.original.received_by_name || '—',
  },
  {
    id: 'actions',
    header: '',
    meta: { align: 'right', cellClass: 'whitespace-nowrap' },
    cell: ({ row, table }) => {
      const { printingId, onPrintQrs } = table.options.meta as TableMeta;
      const item = row.original;
      const busy = printingId === item.id;
      return (
        <span className="inline-flex items-center gap-1">
          {item.tracked_per_instance && (
            <button
              type="button"
              onClick={() => onPrintQrs(item)}
              disabled={printingId !== null}
              aria-label={`Print item QRs for ${itemLabel(item)}`}
              className={buttonClass('quiet', 'sm')}
            >
              {busy ? 'Printing…' : 'Print item QRs'}
            </button>
          )}
          <Link
            to={`/receiving/item/${item.id}`}
            aria-label={`View ${itemLabel(item)}`}
            className={buttonClass('quiet', 'sm')}
          >
            View
          </Link>
        </span>
      );
    },
  },
];

const ReceiptBoxPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const api = useApi();
  const toast = useToast();
  const history = useHistory();
  const canSeePOs = useUserStore((s) => s.hasCapability('procurement'));

  const [detail, setDetail] = useState<ReceiptBoxDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [printingId, setPrintingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      // Cleared first: a failed load for box B must not leave box A on screen
      // under B's URL, with a reprint button that prints A.
      setDetail(null);
      try {
        const data = (await api.get(`/receipt-boxes/${id}`)) as ReceiptBoxDetail;
        if (!cancelled) setDetail(data);
      } catch (err: any) {
        if (cancelled) return;
        if (err?.status === 404) setNotFound(true);
        else toast.error(err?.data?.error?.message || err?.message || 'Could not load this box. Reload to try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const barcode = detail?.box.barcode;

  // Instances are not on the box payload; the by-barcode read carries them per
  // item, and reading at click time means a sheet never prints stale ids.
  const handlePrintQrs = useCallback(async (item: ReceiptBoxItem) => {
    if (!barcode) return;
    setPrintingId(item.id);
    try {
      const resolved = (await api.get(
        `/receipt-boxes/by-barcode/${encodeURIComponent(barcode)}`
      )) as BoxByBarcode;
      const instances = resolved.items.find((i) => i.id === item.id)?.instances ?? [];
      if (instances.length === 0) {
        toast.warning(`${itemLabel(item)} has no item records to print QRs for.`);
        return;
      }
      await printBoxInstanceQRLabels(instances, { sku: itemLabel(item), barcode });
    } catch (err: any) {
      toast.error(err?.message || 'The QR sheet did not print. Try again.');
    } finally {
      setPrintingId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barcode]);

  const handleReprint = async () => {
    if (!detail) return;
    try {
      await openAuthedPdf(`/api/receipt-boxes/${detail.box.id}/reprint-label`, {
        method: 'POST',
        followJsonRedirect: true,
      });
      toast.success(`Reprinted the sticker for ${detail.box.barcode}`);
    } catch (err: any) {
      toast.error(err?.message || 'The sticker did not reprint. Try again.');
    }
  };

  if (loading) return <LogoSpinner label="Loading the box" />;

  if (notFound || !detail) {
    return (
      <PageLayout kind="record" className="pt-4">
        <EmptyState
          className="max-w-xl"
          title={notFound ? 'There is no box with this id.' : 'This box did not load.'}
          action={<BackLink to="/receiving" label="Receiving" />}
        >
          {notFound
            ? 'The link may be old. Scan the sticker from the Receiving Hub to find it.'
            : 'Check your connection and reload the page.'}
        </EmptyState>
      </PageLayout>
    );
  }

  const { box, items, delivery_boxes } = detail;
  const qcBox = items.length > 0 && items.every((i) => i.disposition === 'rejected');

  const position = qcBox
    ? 'A QC Hold box'
    : box.box_no != null && box.box_count != null
      ? `Box ${box.box_no} of ${box.box_count}`
      : 'A box';
  const received = formatTimestamp(box.received_at, 'MMM D, YYYY');
  // The QC-hold box numbers itself 1 of 1; it goes last and says what it is.
  const siblings = [...delivery_boxes].sort(
    (a, b) => Number(Boolean(a.qc_hold)) - Number(Boolean(b.qc_hold)) || (a.box_no ?? 0) - (b.box_no ?? 0)
  );
  const trackedCount = items.filter((i) => i.tracked_per_instance).length;
  const tableMeta: TableMeta = { printingId, onPrintQrs: handlePrintQrs };

  return (
    <PageLayout kind="record" className="pt-4" header={(
      <PageHeader
        className="mb-5"
        back={{ to: "/receiving", label: "Receiving" }}
        title={`Box ${box.barcode}`}
        helpTitle="About this box"
        help={
          <>
            <p>
              A box is the physical thing with the sticker on it. An item is what is inside:
              one part, how much of it arrived, and how much is left. This box holds{' '}
              {items.length} items, so its sticker lands here rather than on any one of them.
            </p>
            <p>
              Stock, history and corrections belong to each item. Open one with View, or
              select a row and press Enter.
            </p>
            <p>
              Reprint box sticker prints this box&apos;s sticker again. Print item QRs prints
              the per-unit QR sheet for one tracked item, bookended with its name and this
              box&apos;s barcode.
            </p>
          </>
        }
        lede={
          <>
            {position}
            {box.vendor_name && <> from {box.vendor_name}</>}
            {box.po_number && (
              <>
                {' '}on{' '}
                {canSeePOs && box.purchase_order_id ? (
                  <Link
                    to={`/procurement/po/${box.purchase_order_id}`}
                    className={`font-mono text-primary hover:underline ${FOCUS}`}
                  >
                    {box.po_number}
                  </Link>
                ) : (
                  <span className="font-mono">{box.po_number}</span>
                )}
              </>
            )}
            , received {received}
            {box.received_by_name && <> by {box.received_by_name}</>}.
          </>
        }
        meta={
          (box.slip_number || box.tracking_number) && (
            <>
              {box.slip_number && (
                <span className="rounded-full bg-surface-container-high px-2.5 py-1 text-xs text-on-surface-variant">
                  Slip <span className="font-mono text-on-surface">{box.slip_number}</span>
                </span>
              )}
              {box.tracking_number && (
                <span className="rounded-full bg-surface-container-high px-2.5 py-1 text-xs text-on-surface-variant">
                  {box.carrier ? `${box.carrier} tracking` : 'Tracking'}{' '}
                  <span className="font-mono text-on-surface">{box.tracking_number}</span>
                </span>
              )}
            </>
          )
        }
        actions={<SecondaryAction onClick={handleReprint}>Reprint box sticker</SecondaryAction>}
      />
    )}>
    <div className="space-y-5">
      {box.notes && (
        <p className="max-w-2xl text-sm text-on-surface-variant whitespace-pre-line">{box.notes}</p>
      )}

      <section aria-label="Items in this box">
        <DataTable<ReceiptBoxItem>
          data={items}
          columns={ITEM_COLUMNS}
          getRowId={(i) => i.id}
          meta={tableMeta}
          strip={
            <h2 className="text-sm font-semibold text-on-surface">
              {items.length} items in this box
              {trackedCount > 0 && (
                <span className="ml-2 font-normal text-on-surface-variant">
                  {trackedCount} tracked per unit
                </span>
              )}
            </h2>
          }
          empty={
            <EmptyState variant="area">
              Nothing has been filed into this box. Receive against its order from the Receiving Hub.
            </EmptyState>
          }
          emptyMode="replace"
        />
      </section>

      {siblings.length > 1 && (
        <section aria-labelledby="delivery-boxes-heading" className="space-y-2">
          <h2 id="delivery-boxes-heading" className="text-sm font-semibold text-on-surface">
            Other boxes in this delivery
          </h2>
          <nav aria-label="Boxes in this delivery">
            <ul className="inline-flex flex-wrap gap-[2px] rounded-lg bg-surface-container-low p-[2px] shadow-[inset_0_1px_2px_rgba(0,0,0,.35)]">
              {siblings.map((b) => {
                const label = b.qc_hold ? 'QC Hold' : b.box_no != null ? `Box ${b.box_no}` : 'Box';
                if (b.id === box.id) {
                  return (
                    <li key={b.id}>
                      <span
                        aria-current="page"
                        className="flex items-baseline gap-2 rounded-md bg-primary/15 px-3 py-1.5 text-xs text-primary-ink"
                      >
                        {label}
                        <span className="font-mono">{b.barcode}</span>
                        <span className="sr-only">(this box)</span>
                      </span>
                    </li>
                  );
                }
                return (
                  <li key={b.id}>
                    <Link
                      to={`/receiving/box/${b.id}`}
                      className={`flex items-baseline gap-2 rounded-md px-3 py-1.5 text-xs text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface ${FOCUS}`}
                    >
                      {label}
                      <span className="font-mono">{b.barcode}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
        </section>
      )}
    </div>
    </PageLayout>
  );
};

export default ReceiptBoxPage;
